import { useState, useEffect, useCallback, useMemo } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, Trash2, Plus, Clock, Mail, UserCheck, UserPlus, Calendar, CheckCircle2, AlertTriangle, Loader2, Unlink, Phone, Pencil, X, MessageCircle, Send, Video, Copy, RefreshCw, Share2, Lock } from "lucide-react";
import { toast } from "sonner";
import * as api from "@/lib/api";

const patientName = (p) => [p.cognome, p.nome].filter(Boolean).join(" ") || "Senza nome";
const patientEmail = (p) => (p.anamnesi && p.anamnesi.generali && p.anamnesi.generali.email) || "";
const patientPhone = (p) => (p.anamnesi && p.anamnesi.generali && p.anamnesi.generali.cellulare) || "";

const DURATIONS = [30, 45, 60, 90];
const toMin = (hhmm) => { const [h, m] = (hhmm || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const fromMin = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const SLOT_START = 8 * 60;   // 08:00
const SLOT_END = 21 * 60;    // 21:00
const SLOT_STEP = 45;        // slot ogni 45 minuti
const emptyForm = { patientId: "", nome: "", cognome: "", email: "", phone: "", date: "", time: "", duration: 60, note: "" };

const sharedPrivate = (a) => a.workspace === "private" && api.getWorkspace() !== "private";

export default function AppuntamentiDrawer({ open, setOpen, patients, onChanged, focusId }) {
  const [mode, setMode] = useState("registered");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [highlightId, setHighlightId] = useState(null);
  const [cal, setCal] = useState({ connected: false, email: "" });
  const [conflict, setConflict] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try { setAppointments(await api.listAppointments()); } catch (e) { console.error("Errore appuntamenti:", e); }
  }, []);
  const [syncing, setSyncing] = useState(false);
  const syncNow = async () => {
    setSyncing(true);
    try { await refresh(); await refreshCal(); onChanged && onChanged(); toast.success("Appuntamenti sincronizzati con Google Calendar"); }
    catch (e) { console.error("Errore sync:", e); toast.error("Sincronizzazione non riuscita"); }
    finally { setSyncing(false); }
  };
  const refreshCal = useCallback(async () => {
    try { setCal(await api.calendarStatus()); } catch (e) { console.error("Errore stato calendar:", e); }
  }, []);

  useEffect(() => { if (open) { refresh(); refreshCal(); } }, [open, refresh, refreshCal]);

  // Scroll to and highlight the appointment opened from a notification
  useEffect(() => {
    if (!open || !focusId || appointments.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="appt-item-${focusId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(focusId);
      setTimeout(() => setHighlightId(null), 2800);
    }, 350);
    return () => clearTimeout(t);
  }, [open, focusId, appointments]);

  // Detect return from OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("calendar");
    if (c === "connected") { toast.success("Google Calendar collegato"); setOpen(true); refreshCal(); }
    else if (c === "error") { toast.error("Collegamento a Google Calendar non riuscito"); }
    if (c) { params.delete("calendar"); window.history.replaceState({}, "", window.location.pathname); }
  }, [refreshCal, setOpen]);

  // Check conflicts when date+time set and calendar connected
  useEffect(() => {
    setConflict(null);
    if (!cal.connected || !form.date || !form.time) return;
    let active = true;
    setChecking(true);
    api.calendarConflicts(form.date, form.time, form.duration)
      .then((r) => { if (active) setConflict(r); })
      .catch(() => {})
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [cal.connected, form.date, form.time, form.duration]);

  // Free 45-minute slots for the selected day, excluding already-booked appointments
  const freeSlots = useMemo(() => {
    if (!form.date) return [];
    const dur = form.duration || SLOT_STEP;
    const busy = appointments
      .filter((a) => a.date === form.date && a.id !== editingId)
      .map((a) => { const s = toMin(a.time); return [s, s + (a.duration || 60)]; });
    const out = [];
    for (let t = SLOT_START; t + dur <= SLOT_END; t += SLOT_STEP) {
      const overlaps = busy.some(([bs, be]) => t < be && t + dur > bs);
      if (!overlaps) out.push(fromMin(t));
    }
    return out;
  }, [form.date, form.duration, appointments, editingId]);
  const slotOccupied = (t) => {
    if (!form.date || !t) return false;
    const dur = form.duration || SLOT_STEP;
    const s = toMin(t);
    return appointments
      .filter((a) => a.date === form.date && a.id !== editingId)
      .some((a) => { const bs = toMin(a.time); const be = bs + (a.duration || 60); return s < be && s + dur > bs; });
  };
  const timeOptions = form.time && !freeSlots.includes(form.time) && !slotOccupied(form.time) ? [form.time, ...freeSlots] : freeSlots;

  const connect = async () => {
    try {
      const { authorization_url } = await api.calendarLoginUrl();
      window.location.href = authorization_url;
    } catch (e) { toast.error("Impossibile avviare il collegamento a Google"); }
  };
  const disconnect = async () => {
    try { await api.calendarDisconnect(); setCal({ connected: false, email: "" }); toast.success("Google Calendar scollegato"); } catch (e) { toast.error("Errore"); }
  };

  const selectPatient = (id) => {
    const p = patients.find((x) => x.id === id);
    setForm((f) => ({ ...f, patientId: id, nome: p ? p.nome : "", cognome: p ? p.cognome : "", email: p ? patientEmail(p) : "", phone: p ? patientPhone(p) : "" }));
  };

  const save = async () => {
    if (!form.date || !form.time) { toast.error("Inserisci giorno e ora dell'appuntamento"); return; }
    const registered = mode === "registered";
    if (registered && !form.patientId) { toast.error("Seleziona un paziente registrato"); return; }
    if (!registered && !form.nome.trim() && !form.cognome.trim()) { toast.error("Inserisci nome e cognome"); return; }
    if (conflict && conflict.conflict) {
      const ok = window.confirm("C'è già un impegno su Google Calendar in questo orario. Vuoi comunque salvare l'appuntamento?");
      if (!ok) return;
    }
    setSaving(true);
    try {
      await api.createAppointment(editingId ? { ...form, id: editingId, registered } : { ...form, registered });
      toast.success(editingId ? "Appuntamento aggiornato" : (cal.connected ? "Appuntamento salvato e aggiunto a Google Calendar" : "Appuntamento salvato"));
      setForm(emptyForm);
      setEditingId(null);
      setConflict(null);
      refresh();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Errore nel salvataggio dell'appuntamento");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setMode(a.registered ? "registered" : "guest");
    setForm({
      patientId: a.patientId || "",
      nome: a.nome || "",
      cognome: a.cognome || "",
      email: a.email || "",
      phone: a.phone || "",
      date: a.date || "",
      time: a.time || "",
      duration: a.duration || 60,
      note: a.note || "",
    });
    if (typeof document !== "undefined") {
      const el = document.querySelector('[data-testid="appuntamenti-drawer"]');
      if (el) el.scrollTop = 0;
    }
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); setConflict(null); };

  const remove = async (id) => {
    try { await api.deleteAppointment(id); if (editingId === id) cancelEdit(); refresh(); onChanged && onChanged(); } catch (e) { toast.error("Errore eliminazione"); }
  };

  const waLink = (phone) => {
    const digits = (phone || "").replace(/[^\d]/g, "");
    return digits ? `https://wa.me/${digits}` : null;
  };
  const tgLink = (phone) => {
    const digits = (phone || "").replace(/[^\d]/g, "");
    return digits ? `tg://resolve?phone=${digits}` : null;
  };

  const fmtDate = (d) => { const x = new Date(d); return isNaN(x) ? d : x.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }); };
  const apptName = (a) => [a.cognome, a.nome].filter(Boolean).join(" ") || "Senza nome";

  const shareAppt = (a) => {
    const nome = [a.nome, a.cognome].filter(Boolean).join(" ") || "Appuntamento";
    const lines = [
      `📅 Appuntamento — ${nome}`,
      `🗓️ ${fmtDate(a.date)} alle ${a.time} (${a.duration} min)`,
    ];
    if (a.meetLink) lines.push(`🎥 Google Meet: ${a.meetLink}`);
    if (a.note) lines.push(`📝 ${a.note}`);
    const text = lines.join("\n");
    if (navigator.share) {
      navigator.share({ title: `Appuntamento ${nome}`, text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success("Dettagli appuntamento copiati")).catch(() => toast.error("Impossibile copiare"));
    }
  };
  const fmtTime = (iso) => { const x = new Date(iso); return isNaN(x) ? iso : x.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="appuntamenti-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground">
            <CalendarPlus className="h-5 w-5 text-primary" /> Appuntamenti
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5 overflow-y-auto pb-6" style={{ maxHeight: "calc(100vh - 120px)" }}>
          {/* Google Calendar connection */}
          <div className="rounded-lg border border-white/10 bg-black/20 p-3" data-testid="calendar-connection">
            {cal.connected ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                  <span className="min-w-0">
                    <span className="font-semibold">Google Calendar collegato</span>
                    {cal.email && <span className="block truncate text-[11px] text-muted-foreground">{cal.email}</span>}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button onClick={syncNow} disabled={syncing} className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20 disabled:opacity-50" data-testid="calendar-sync-btn">
                    {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sincronizza
                  </button>
                  <button onClick={disconnect} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-destructive" data-testid="calendar-disconnect-btn">
                    <Unlink className="h-3.5 w-3.5" /> Scollega
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" /> Collega il tuo Google Calendar per evitare sovrapposizioni.
                </div>
                <button onClick={connect} className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/20" data-testid="calendar-connect-btn">
                  <Calendar className="h-4 w-4" /> Connetti Google Calendar
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
            {[["registered", "Paziente registrato", UserCheck], ["guest", "Non registrato", UserPlus]].map(([v, l, Icon]) => (
              <button key={v} onClick={() => { setMode(v); setForm(emptyForm); }} data-testid={`appt-mode-${v}`}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${mode === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>

          <section className="panel space-y-3 p-4">
            {mode === "registered" ? (
              <>
                <div>
                  <div className="field-label mb-1">Paziente</div>
                  <Select value={form.patientId} onValueChange={selectPatient}>
                    <SelectTrigger className="lab-input h-auto" data-testid="appt-patient-select"><SelectValue placeholder="Seleziona paziente" /></SelectTrigger>
                    <SelectContent>
                      {patients.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nessun paziente registrato</div>}
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{patientName(p)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="field-label mb-1">Email (da Anamnesi)</div>
                  <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm" data-testid="appt-email-auto">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className={form.email ? "" : "text-muted-foreground"}>{form.email || "Nessuna email in Anamnesi"}</span>
                  </div>
                </div>
                <div>
                  <div className="field-label mb-1">Cellulare (da Anamnesi)</div>
                  <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm" data-testid="appt-phone-auto">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className={form.phone ? "" : "text-muted-foreground"}>{form.phone || "Nessun cellulare in Anamnesi"}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="field-label mb-1">Nome</div>
                    <input className="lab-input" placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="appt-nome-input" />
                  </div>
                  <div>
                    <div className="field-label mb-1">Cognome</div>
                    <input className="lab-input" placeholder="Cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} data-testid="appt-cognome-input" />
                  </div>
                </div>
                <div>
                  <div className="field-label mb-1">Email</div>
                  <input type="email" className="lab-input" placeholder="es. mario.rossi@email.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="appt-email-input" />
                </div>
                <div>
                  <div className="field-label mb-1">Cellulare</div>
                  <input type="tel" className="lab-input" placeholder="es. +39 333 1234567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="appt-phone-input" />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="field-label mb-1">Giorno appuntamento</div>
                <input type="date" className="lab-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appt-date-input" />
              </div>
              <div>
                <div className="field-label mb-1">Ora appuntamento</div>
                <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })} disabled={!form.date}>
                  <SelectTrigger className="lab-input h-auto" data-testid="appt-time-input"><SelectValue placeholder={form.date ? "Seleziona slot libero" : "Scegli prima il giorno"} /></SelectTrigger>
                  <SelectContent>
                    {timeOptions.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Nessuno slot libero in questo giorno</div>
                    ) : (
                      timeOptions.map((t) => <SelectItem key={t} value={t} data-testid={`appt-time-slot-${t}`}>{t}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="field-label mb-1">Durata</div>
              <div className="flex gap-1 rounded-lg border border-white/10 bg-black/25 p-1">
                {DURATIONS.map((d) => (
                  <button key={d} onClick={() => setForm({ ...form, duration: d })} data-testid={`appt-duration-${d}`}
                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${form.duration === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{d} min</button>
                ))}
              </div>
            </div>

            {/* Conflict feedback */}
            {cal.connected && form.date && form.time && (
              <div data-testid="appt-conflict-status">
                {checking ? (
                  <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifica disponibilità su Google Calendar…
                  </div>
                ) : conflict && conflict.conflict ? (
                  <div className="rounded-lg border border-[hsl(0_72%_55%)]/30 bg-[hsl(0_72%_40%)]/10 px-3 py-2 text-xs text-[hsl(0_72%_72%)]" data-testid="appt-conflict-warning">
                    <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Orario occupato su Google Calendar</div>
                    {conflict.events.map((e) => (
                      <div key={`${e.summary}-${e.start}-${e.end}`} className="mt-1 pl-5 text-[hsl(0_72%_80%)]">• {e.summary} ({fmtTime(e.start)}–{fmtTime(e.end)})</div>
                    ))}
                  </div>
                ) : conflict ? (
                  <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary" data-testid="appt-conflict-free">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Orario libero su Google Calendar
                  </div>
                ) : null}
              </div>
            )}

            <div>
              <div className="field-label mb-1">Note (es. motivo della visita)</div>
              <textarea rows={2} className="lab-input resize-none" placeholder="es. Controllo mensile, dolori articolari…" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="appt-note-input" />
            </div>

            <div className="flex gap-2">
              <button onClick={save} disabled={saving} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60" data-testid="appt-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {editingId ? "Aggiorna appuntamento" : "Aggiungi appuntamento"}
              </button>
              {editingId && (
                <button onClick={cancelEdit} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/5" data-testid="appt-cancel-edit-btn">
                  <X className="h-4 w-4" /> Annulla
                </button>
              )}
            </div>
          </section>

          <div>
            <h3 className="mb-2 font-display text-sm font-bold">Prossimi appuntamenti <span className="ml-1 rounded-md bg-primary/15 px-1.5 py-0.5 text-[11px] font-bold text-primary">{appointments.length}</span></h3>
            {appointments.length === 0 ? (
              <div className="rounded-lg border border-dashed border-white/10 py-8 text-center text-sm text-muted-foreground">Nessun appuntamento in agenda</div>
            ) : (
              <div className="space-y-1.5">
                {appointments.map((a) => (
                  <div key={a.id} className={`flex items-center justify-between gap-2 rounded-lg border bg-black/20 px-3 py-2 transition-all ${highlightId === a.id ? "border-primary/70 ring-2 ring-primary/60" : (sharedPrivate(a) ? "border-2 border-[hsl(43_74%_58%)] shadow-[0_0_0_1px_hsl(43_74%_58%/0.25)]" : "border-white/10")}`} data-testid={`appt-item-${a.id}`} data-private={sharedPrivate(a) ? "true" : "false"}>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">
                        {sharedPrivate(a) && <Lock className="mr-1 inline h-3 w-3 text-[hsl(43_74%_58%)]" data-testid={`appt-private-${a.id}`} />}
                        {apptName(a)}
                        {a.registered ? <span className="ml-1 rounded bg-primary/15 px-1 py-0.5 text-[9px] font-bold uppercase text-primary">registrato</span> : null}
                        {a.source === "google" ? <span className="ml-1 rounded bg-[hsl(var(--c-teal))]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[hsl(var(--c-teal))]">google</span> : (a.googleEventId ? <span className="ml-1 rounded bg-[hsl(var(--c-teal))]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[hsl(var(--c-teal))]">calendar</span> : null)}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><CalendarPlus className="h-3 w-3" /> {fmtDate(a.date)}</span>
                        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.time}{a.duration ? ` · ${a.duration}′` : ""}</span>
                      </div>
                      {a.phone && (
                        <div className="mt-0.5 flex items-center gap-2">
                          <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--c-teal))] hover:underline" data-testid={`appt-phone-${a.id}`}>
                            <Phone className="h-3 w-3" /> {a.phone}
                          </a>
                          {waLink(a.phone) && (
                            <a href={waLink(a.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(145_63%_55%)] hover:underline" data-testid={`appt-whatsapp-${a.id}`}>
                              <MessageCircle className="h-3 w-3" /> WhatsApp
                            </a>
                          )}
                          {tgLink(a.phone) && (
                            <a href={tgLink(a.phone)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(200_80%_55%)] hover:underline" data-testid={`appt-telegram-${a.id}`}>
                              <Send className="h-3 w-3" /> Telegram
                            </a>
                          )}
                        </div>
                      )}
                      {a.meetLink && (
                        <div className="mt-0.5 flex items-center gap-3">
                          <a href={a.meetLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--c-teal))] hover:underline" data-testid={`appt-meet-${a.id}`}>
                            <Video className="h-3 w-3" /> Google Meet
                          </a>
                          <button type="button" onClick={() => { navigator.clipboard.writeText(a.meetLink).then(() => toast.success("Link Meet copiato")).catch(() => toast.error("Impossibile copiare")); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-primary" data-testid={`appt-meet-copy-${a.id}`}>
                            <Copy className="h-3 w-3" /> Copia link
                          </button>
                        </div>
                      )}
                      {a.email && <div className="truncate text-[11px] text-muted-foreground">{a.email}</div>}
                      {a.note && <div className="mt-0.5 text-[11px] italic text-muted-foreground" data-testid={`appt-note-${a.id}`}>“{a.note}”</div>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {a.email && (
                        <button onClick={() => shareAppt(a)} className="inline-flex items-center gap-1 rounded-md border border-primary/50 bg-primary/15 px-2 py-1.5 text-xs font-semibold text-primary shadow-[0_0_0_1px_hsl(var(--primary)/0.15)] transition-colors hover:bg-primary/25" title="Condividi appuntamento" data-testid={`appt-share-${a.id}`}>
                          <Share2 className="h-3.5 w-3.5" /> Condividi
                        </button>
                      )}
                      {a.source !== "google" && (
                        <button onClick={() => startEdit(a)} className="text-muted-foreground transition-colors hover:text-primary" title="Modifica" data-testid={`appt-edit-${a.id}`}><Pencil className="h-4 w-4" /></button>
                      )}
                      <button onClick={() => remove(a.id)} className="text-muted-foreground transition-colors hover:text-destructive" title="Elimina" data-testid={`appt-remove-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
